import { useState, useEffect } from "react";
import GuardianDonationWidget from "@/components/donation/GuardianDonationWidget";
import DiwaliDonationWidget from "@/components/donation/DiwaliDonationWidget";
import DiwaliProgress from "@/components/campaign/DiwaliProgress";
import { useDiwaliCampaign } from "@/hooks/useDiwaliCampaign";
import { resolveImageWithFallback } from "@/lib/seo";

export function Hero() {
  const { status } = useDiwaliCampaign();
  const diwaliActive = status?.active;
  const [heroImg, setHeroImg] = useState("/images/sanctuary.jpg");

  useEffect(() => {
    if (diwaliActive) {
      resolveImageWithFallback("/images/diwali-hero.jpg", "/images/sanctuary.jpg", setHeroImg);
    } else {
      setHeroImg("/images/sanctuary.jpg");
    }
  }, [diwaliActive]);

  return (
    <section className="relative md:min-h-screen md:flex md:items-end" data-testid="landing-hero">
      <div className="relative h-[46vh] w-full md:absolute md:inset-0 md:h-full">
        <img
          src={heroImg}
          alt="AMTM founder embracing a rescued dog at the sanctuary, surrounded by rehabilitated street dogs"
          className="absolute inset-0 w-full h-full object-cover object-[50%_88%] md:object-center"
          loading="eager"
          fetchPriority="high"
        />
        <div
          className={
            diwaliActive
              ? "absolute inset-0 bg-black/15 md:bg-black/65"
              : "absolute inset-0 bg-gradient-to-t from-black/15 to-transparent md:bg-gradient-to-tr md:from-black/85 md:via-black/45 md:to-black/5"
          }
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 pb-10 md:pb-24 pt-8 md:pt-32 w-full grid lg:grid-cols-2 gap-8 lg:gap-14 items-end">
        <div className="max-w-2xl">
          {diwaliActive ? (
            <div data-testid="landing-hero-diwali-variant" className="md:[text-shadow:0_1px_3px_rgba(0,0,0,0.5)]">
              <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-amtm-teal md:text-amtm-tealLight mb-5">
                This Diwali &middot; Animal Matter To Me
              </p>
              <h1
                className="font-display text-[clamp(2.25rem,6vw,4rem)] leading-[1.1] tracking-tight text-amtm-text md:text-amtm-bg mb-6"
                data-testid="landing-hero-headline"
              >
                This Diwali, fill 500+ bowls with a festive meal.
              </h1>
              <p className="text-base md:text-lg text-amtm-textMuted md:text-amtm-bg/85 max-w-lg mb-4 leading-relaxed">
                And say thank you to the caregivers who feed, clean and treat our rescued animals every day of the year.
              </p>
              <DiwaliProgress variant="hero" />
            </div>
          ) : (
            <div data-testid="landing-hero-default-variant">
              <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-amtm-teal md:text-amtm-tealLight md:drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)] mb-5">
                Animal Matter To Me &middot; Mumbai &middot; Since 2010
              </p>
              <h1
                className="font-display text-[clamp(2.25rem,6vw,4rem)] leading-[1.1] tracking-tight text-amtm-text md:text-amtm-bg mb-6"
                data-testid="landing-hero-headline"
              >
                Would you like to become one of the people who keeps 500+ rescued animals alive every single day?
              </h1>
              <p className="text-base md:text-lg text-amtm-textMuted md:text-amtm-bg/85 max-w-lg mb-2 leading-relaxed">
                AMTM Guardians give monthly, so we can plan ahead, keep our vets, and never turn away an emergency.
              </p>
            </div>
          )}
        </div>
        <div className="w-full max-w-md lg:ml-auto">
          {diwaliActive ? <DiwaliDonationWidget testIdPrefix="guardian-hero" /> : <GuardianDonationWidget testIdPrefix="guardian-hero" />}
        </div>
      </div>
    </section>
  );
}
